"use client";

import { useState } from "react";
import { LayoutGrid, Maximize2 } from "lucide-react";
import { ImageFullScreenViewer } from "@/components/shared/ImageFullScreenViewer";

interface Props {
  diagramUrl: string;
  /** The garden render (summer, latest edit) shown next to the diagram. */
  gardenUrl?: string;
}

/**
 * The outdoor planting diagram as its own card under the shopping list, with
 * the same header and position as the container result's `PlantingDiagram`.
 * It sits side by side with the garden render so the numbers on the plan can
 * be read against the picture. The diagram is not a hero view: the hero's
 * before/after slider compares against the original photo, which means
 * nothing for a diagram.
 */
export function PlantingGuideCard({ diagramUrl, gardenUrl }: Props) {
  const [fullScreen, setFullScreen] = useState<string | null>(null);

  const panels = [
    ...(gardenUrl
      ? [
          {
            key: "garden",
            label: "Your garden",
            url: gardenUrl,
            alt: "Your garden design",
          },
        ]
      : []),
    {
      key: "diagram",
      label: "Planting diagram",
      url: diagramUrl,
      alt: "Planting diagram, numbered like your plant list",
    },
  ];

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-plantista-800 dark:bg-plantista-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-xl font-bold text-plantista-950 dark:text-white">
            <LayoutGrid
              className="h-5 w-5 text-plantista-600 dark:text-plantista-400"
              aria-hidden
            />
            Planting Guide
          </h3>
          <p className="mt-0.5 text-sm text-plantista-600 dark:text-plantista-300">
            Top view, numbered like your plant list (not to scale)
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFullScreen(diagramUrl)}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-plantista-700 hover:bg-plantista-100 dark:text-plantista-300 dark:hover:bg-plantista-800"
        >
          <Maximize2 className="h-4 w-4" aria-hidden />
          View larger
        </button>
      </div>

      {/* The diagram leads and takes most of the width: its labels are baked
          into the image, so width is the only thing that makes them readable.
          The garden render sits beside it from lg up, below it otherwise. */}
      <div
        className={`mt-4 grid gap-4 ${panels.length > 1 ? "lg:grid-cols-3" : ""}`}
      >
        {[...panels].reverse().map((p) => (
          <figure
            key={p.key}
            className={`min-w-0 ${p.key === "diagram" && panels.length > 1 ? "lg:col-span-2" : ""}`}
          >
            <button
              type="button"
              onClick={() => setFullScreen(p.url)}
              className="block w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-plantista-800 dark:bg-plantista-950"
              aria-label={`View ${p.label.toLowerCase()} full screen`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={p.url}
                alt={p.alt}
                className={`w-full ${p.key === "diagram" ? "h-auto" : "aspect-[4/3] object-cover"}`}
              />
            </button>
            <figcaption className="mt-1.5 text-xs font-semibold uppercase tracking-wider text-plantista-600 dark:text-plantista-300">
              {p.label}
            </figcaption>
          </figure>
        ))}
      </div>

      <ImageFullScreenViewer
        imageUrl={fullScreen || ""}
        alt="Planting guide"
        isOpen={!!fullScreen}
        onClose={() => setFullScreen(null)}
      />
    </section>
  );
}
